import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "../../styles/SidebarModern.css";

import usePermissions from "../../hook/usePermissions";
// Definicion UNICA de "usuario administrativo" (antes duplicada aqui y en
// Layout/Admin.js). Mismos permisos, mismo comportamiento.
import { ADMIN_AREA_PERMISSIONS } from "../../utils/permisosAdmin";

// SIDEBAR-MENU-UX-FIX-01
// Sidebar 100% React: acordeon controlado + route-aware + scroll interno nativo.
// Ya NO participan librerias jQuery del tema para el menu ni el scroll -- ver
// Admin.js (se removio ese init) y el atributo de scroll legacy (removido de
// aqui). El estado del acordeon es una unica fuente de verdad: `openGroup`.

// RBAC-CUTOVER-03/07: catalogo de permisos "administrativos" para decidir la
// visibilidad de "Configuración" -- NINGUNO inventado (ver src/rbac/constants.ts
// y el mismo catalogo en Admin.js). `clinics.view` (singular) queda FUERA a
// proposito: DOCTOR tambien lo tiene.

// REDISENO-SMARTMEDIC FASE 2: el menu se agrupa por lo que la gente viene a
// hacer -- "Atencion clinica" y "Administracion" -- en vez de un unico bloque
// "MENU" que no dice nada. Configuracion sigue al final, tras su separador.
// Es SOLO reorganizacion visual: mismos items, mismas rutas y EXACTAMENTE los
// mismos permisos (este cambio no toca RBAC).
const SECCIONES = [
  { key: "clinica", label: "Atención clínica" },
  { key: "admin", label: "Administración" },
];

// Estructura declarativa del menu. Cada item: link simple {label, icon, path,
// permission} o grupo {key, label, icon, children:[...]}, mas la `seccion` a la
// que pertenece.
const MENU = [
  { key: "pacientes", seccion: "clinica", label: "Pacientes", icon: "bx-user", path: "/pacientes", permission: "pacientes.view" },
  // CITAS-VISTA-MEDICO: dos pantallas distintas con permisos distintos.
  // "Citas" es la agenda de TODA la clínica -- herramienta de recepción --, y
  // "Mi agenda" la del propio médico. El rol Doctor tiene el segundo permiso y
  // no el primero, así que ve una y no la otra sin que el menú tenga que saber
  // nada de médicos: manda el permiso, como en el resto del menú.
  { key: "citas", seccion: "clinica", label: "Citas", icon: "bx-calendar", path: "/gestion_citas", permission: "citas.agenda.view" },
  { key: "mi-agenda", seccion: "clinica", label: "Mi agenda", icon: "bx-time-five", path: "/agenda", permission: "citas.mi_agenda.view" },
  { key: "signos", seccion: "clinica", label: "Signos Vitales", icon: "bx-heart", path: "/sigvital", permission: "signos_vitales.view" },
  { key: "resultados", seccion: "clinica", label: "Resultados", icon: "bx-test-tube", path: "/resultados", permission: "resultados.view" },
  {
    key: "facturas", seccion: "admin", label: "Facturas", icon: "bx-file",
    children: [
      { label: "Listar", path: "/factura-listar", permission: "billing.view" },
      { label: "Listar Por Cobrar", path: "/factura-listar-por-cobrar", permission: "billing.view" },
      { label: "Crear", path: "/factura-crear", permission: "billing.create" },
      { label: "Clientes", path: "/clientes", permission: "clients.view" },
    ],
  },
  {
    key: "reportes", seccion: "admin", label: "Reportes", icon: "bx-bar-chart-alt-2",
    children: [
      { label: "Matriz Atenciones", path: "/matrizatenciones", permission: "reportes.view" },
      { label: "Reporte General de Atenciones", path: "/reportes/coberturas", permission: "reportes.view" },
      { label: "Producción / Facturación", path: "/reportes/produccion-facturacion", permission: "reportes.view" },
      { label: "Cartera por Aseguradora", path: "/reportes/cartera-aseguradoras", permission: "reportes.view" },
      { label: "Documentos Anulados", path: "/reportes/documentos-anulados", permission: "reportes.view" },
      { label: "Matriz Seguros", path: "/factura-listar-por-cobrar", permission: "billing.reports.view" },
    ],
  },
  // AUDITORIA-CLINICA-FASE-1: modulo independiente de solo lectura, gateado
  // por su propio permiso (nunca citas.view/citas.edit) -- ver
  // context/37_AUDITORIA_CLINICA_FLUJO_GLOBAL_35.md.
  // AUDITORIA-CLINICA-REDISENO: una sola entrada. `/auditoria-clinica` sirve la
  // bandeja rediseñada; el listado clásico ya no tiene entrada de menú.
  { key: "auditoria-clinica", seccion: "admin", label: "Auditoría Clínica", icon: "bx-search-alt", path: "/auditoria-clinica", permission: "auditoria.clinica.view" },
  { key: "soporte", seccion: "admin", label: "Soporte", icon: "bx-help-circle", path: "/soporte", permission: "soporte.view" },
];

const CONFIG_ITEM = { key: "configuracion", label: "Configuración", icon: "bx-cog", path: "/configuracion" };

// Todas las rutas del menu (items, hijos y Configuracion). Sirve para resolver
// el activo cuando una ruta es PREFIJO de otra.
function todasLasRutas() {
  const rutas = [CONFIG_ITEM.path];
  for (const it of MENU) {
    if (it.path) rutas.push(it.path);
    for (const c of it.children || []) rutas.push(c.path);
  }
  return rutas;
}

// SIDEBAR-ACTIVO-FIX: el activo es la ruta MAS ESPECIFICA que corresponde a la
// URL, no "toda ruta que sea prefijo". Con `startsWith(path + "/")` a secas,
// estar en `/auditoria-clinica/bandeja` encendia TAMBIEN "Auditoría Clínica"
// (dos items marcados a la vez). Gana el match mas largo; si no hay ninguno,
// no hay activo.
export function rutaActiva(pathname, rutas = todasLasRutas()) {
  let mejor = null;
  for (const ruta of rutas) {
    if (pathname === ruta || pathname.startsWith(ruta + "/")) {
      if (!mejor || ruta.length > mejor.length) mejor = ruta;
    }
  }
  return mejor;
}

// Devuelve la key del grupo al que pertenece una ruta (o null). El primer grupo
// que matchea gana -- "/factura-listar-por-cobrar" aparece en Facturas y en
// Reportes (Matriz Seguros); prevalece Facturas por orden de definicion.
function getGroupForPath(pathname) {
  for (const g of MENU) {
    if (!g.children) continue;
    if (g.children.some((c) => pathname === c.path || pathname.startsWith(c.path + "/"))) return g.key;
  }
  return null;
}

export default function Sidebar() {
  const { hasPermission } = usePermissions();
  const location = useLocation();
  const pathname = location.pathname;

  const [openGroup, setOpenGroup] = useState(() => getGroupForPath(pathname));

  // route-aware: al cambiar de ruta, el acordeon sigue a la ruta -- abre el
  // grupo de la nueva ruta, o lo cierra si la ruta no pertenece a ningun grupo.
  useEffect(() => {
    setOpenGroup(getGroupForPath(pathname));
  }, [pathname]);

  const toggleGroup = (key) => setOpenGroup((cur) => (cur === key ? null : key));

  const activa = rutaActiva(pathname);
  const isActive = (path) => activa === path;

  const canAccessConfiguracion = ADMIN_AREA_PERMISSIONS.some((p) => hasPermission(p));

  // items visibles ya resueltos por RBAC (grupo sin hijos permitidos -> fuera).
  const items = useMemo(() => {
    return MENU.map((it) => {
      if (!it.children) return hasPermission(it.permission) ? it : null;
      const visibleChildren = it.children.filter((c) => hasPermission(c.permission));
      return visibleChildren.length ? { ...it, visibleChildren } : null;
    }).filter(Boolean);
  }, [hasPermission]);

  // scroll del item activo dentro del nav (no del documento).
  const navRef = useRef(null);
  const activeRef = useRef(null);
  useEffect(() => {
    const el = activeRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest", behavior: "auto" });
    }
  }, [pathname, openGroup]);

  return (
    <aside className="vertical-menu">
      <nav className="vertical-menu__nav" ref={navRef} aria-label="Menú principal">
        {SECCIONES.map((seccion) => {
          const itemsSeccion = items.filter((it) => it.seccion === seccion.key);
          // Una seccion cuyos items no pasan el filtro RBAC no se anuncia.
          if (itemsSeccion.length === 0) return null;
          return (
            <React.Fragment key={seccion.key}>
        <p className="vm-section-title">{seccion.label}</p>

        <ul className="vm-list">
          {itemsSeccion.map((it) => {
            if (!it.children) {
              return (
                <li key={it.key}>
                  <Link
                    to={it.path}
                    ref={isActive(it.path) ? activeRef : null}
                    className={`vm-item vm-item--top ${isActive(it.path) ? "is-active" : ""}`}
                  >
                    <i className={`bx ${it.icon} vm-item__icon`} aria-hidden="true" />
                    <span className="vm-item__label">{it.label}</span>
                  </Link>
                </li>
              );
            }

            const open = openGroup === it.key;
            const subId = `vm-submenu-${it.key}`;
            const groupActive = getGroupForPath(pathname) === it.key;
            return (
              <li key={it.key} className={`vm-group ${open ? "is-open" : ""}`}>
                <button
                  type="button"
                  className={`vm-item vm-item--top vm-group__button ${groupActive && !open ? "is-active" : ""}`}
                  aria-expanded={open}
                  aria-controls={subId}
                  onClick={() => toggleGroup(it.key)}
                >
                  <i className={`bx ${it.icon} vm-item__icon`} aria-hidden="true" />
                  <span className="vm-item__label">{it.label}</span>
                  <i className="bx bx-chevron-right vm-group__chevron" aria-hidden="true" />
                </button>

                <div className={`vm-submenu-wrap ${open ? "is-open" : ""}`}>
                  <ul id={subId} className="vm-submenu" aria-hidden={!open}>
                    {it.visibleChildren.map((c) => (
                      <li key={c.path + c.label}>
                        <Link
                          to={c.path}
                          tabIndex={open ? undefined : -1}
                          ref={isActive(c.path) ? activeRef : null}
                          className={`vm-item vm-item--child ${isActive(c.path) ? "is-active" : ""}`}
                        >
                          <span className="vm-item__label">{c.label}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            );
          })}
        </ul>
            </React.Fragment>
          );
        })}

        {canAccessConfiguracion && (
          <>
            <p className="vm-section-title vm-section-title--config">Configuración</p>
            <ul className="vm-list">
              <li>
                <Link
                  to={CONFIG_ITEM.path}
                  ref={isActive(CONFIG_ITEM.path) ? activeRef : null}
                  className={`vm-item vm-item--top ${isActive(CONFIG_ITEM.path) ? "is-active" : ""}`}
                >
                  <i className={`bx ${CONFIG_ITEM.icon} vm-item__icon`} aria-hidden="true" />
                  <span className="vm-item__label">{CONFIG_ITEM.label}</span>
                </Link>
              </li>
            </ul>
          </>
        )}
      </nav>
    </aside>
  );
}
